import { Field } from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import React, { useRef } from "react";
import * as Flex from "@repo/ui/flex";
import classes from "../media-cta.module.css";
import type { TextAlignmentType, JustifyType, AlignType } from "../utils";
import {
  getVerticalAlignment,
  getHeadingAlignment,
  getHorizontalAlignment,
} from "../utils";
import AnimatedCta from "./animated-cta";
import TextContent from "./text-content";

export type TextComponentProps = {
  textAlignment?: Field<string>;
  headline: Field<string>;
  subheading: Field<string>;
  textColour: Field<string>;
  ctaText: Field<string>;
  mobileTextAlignment?: Field<string>;
  desktopTextAlignment?: Field<string>;
  isHero: boolean;
  isMobileView: boolean;
};

const TextComponent = (props: TextComponentProps) => {
  const {
    textAlignment,
    headline,
    subheading,
    textColour,
    ctaText,
    mobileTextAlignment,
    desktopTextAlignment,
    isHero,
    isMobileView,
  } = props;

  const textContainer = useRef<HTMLDivElement>(null);

  const getTextAlignment = (isMobile: boolean) => {
    return isHero
      ? isMobile
        ? (mobileTextAlignment?.value.toLocaleLowerCase() as TextAlignmentType)
        : (desktopTextAlignment?.value.toLocaleLowerCase() as TextAlignmentType)
      : (textAlignment?.value.toLocaleLowerCase() as TextAlignmentType);
  };

  const textAlignmentValue = getTextAlignment(isMobileView);
  const verticalAlignment = getVerticalAlignment(textAlignmentValue);
  const horizontalAlignment = getHorizontalAlignment(textAlignmentValue);
  const headingTextAlignment = getHeadingAlignment(textAlignmentValue);

  const textContent = (
    <TextContent
      headline={headline}
      subheading={subheading}
      textColour={textColour}
      ctaText={ctaText}
      mobileView={isMobileView}
      isHero={isHero}
    />
  );

  const wrappedTextContent = (
    <div
      className={clsx(
        classes.textContentWrapper,
        classes[
          headingTextAlignment === "right"
            ? "textAlignRight"
            : "textAlignCenter"
        ],
      )}
    >
      {textContent}
    </div>
  );

  return (
    <Flex.Container
      column
      className={
        classes[
          isMobileView || !isHero
            ? "ctaTextContainerWrapper"
            : "heroCtaTextContainerWrapper"
        ]
      }
    >
      <Flex.Container
        column
        ref={textContainer}
        data-testid="text-container"
        className={
          classes[
            isMobileView || isHero ? "heroCtaTextContainer" : "ctaTextContainer"
          ]
        }
        justify={verticalAlignment as JustifyType}
        align={horizontalAlignment as AlignType}
      >
        {isHero && !isMobileView ? wrappedTextContent : textContent}
      </Flex.Container>
      {!isMobileView && isHero && (
        <Flex.Container justify="center">
          <AnimatedCta
            textColour={textColour}
            ctaText={ctaText}
            mobileView={isMobileView}
            isHero={isHero}
          />
        </Flex.Container>
      )}
    </Flex.Container>
  );
};

export default TextComponent;
